import { useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";

import {
  useCancelOrder,
  useGetOrderById,
  useGetPaymentMethod,
} from "../api/hooks";
import { LoadingLayout } from "../components/Layouts/LoadingLayout";
import { DashboardPageLayout } from "../components/Layouts/DashboardPageLayout";
import { PageHeading } from "../components/Headings/PageHeading";
import { addOrderAttachments, openLinkExternal } from "../helpers";
import { ShipmentDetails } from "../components/Sections/OrderDetails/ShipmentDetails";
import { Attachments } from "../components/Sections/OrderDetails/Attachments";
import { PaymentDetails } from "../components/Sections/OrderDetails/PaymentDetails";
import { CancelButton } from "../components/Buttons/CancelButton";
import { DeleteItemModal } from "../components/Modals/DeleteItemModal";
import { ShipmentStatusStepper } from "../components/Steppers/ShipmentStatusStepper";
import { carrierDropoffLinks, ORDER_STATUS } from "../constants";

export function Order() {
  const { orderId } = useParams();
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);

  const { isLoading: isGetOrderLoading, data: order } =
    useGetOrderById(orderId);

  const trackingUrl = order?.tracking?.trackingUrl;

  const { isLoading: isGetPaymentMethodLoading, data: paymentMethod } =
    useGetPaymentMethod(order?.charge.paymentMethodId);

  const {
    mutateAsync: cancelOrder,
    isError,
    error,
    isLoading: isCancelOrderLoading,
  } = useCancelOrder();

  const handleOrderCancellation = async () => {
    try {
      await cancelOrder(orderId);
      setIsCancellationModalOpen(false);
    } catch {}
  };

  const isLoading = isGetOrderLoading || isGetPaymentMethodLoading;
  if (isLoading) return <LoadingLayout />;

  const attachments = addOrderAttachments(order);
  const formattedDate = moment(order.orderDate).format("MMMM D, YYYY");

  /* -----------------------------------------
     Cancellation eligibility logic
  ----------------------------------------- */
  const daysSinceOrder = moment().diff(moment(order.orderDate), "days");

  const isCancelled = order.orderStatus === ORDER_STATUS.CANCELLED;
  const isOrderInInitialState = order.shipmentStatus === "created"
  const isWithinCancellationWindow = daysSinceOrder <= 14;
  const canCancelOrder = !isCancelled && isWithinCancellationWindow && isOrderInInitialState;

  return (
    <DashboardPageLayout>
      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-8 max-w-7xl mx-auto space-y-12">
        <PageHeading withBreadcrumbs title="Order Details" />
        {/* -----------------------------
        Cancelled order banner
    ----------------------------- */}
        {isCancelled ? (
          <div className="rounded-md bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">
              This order has been{" "}
              <span className="font-semibold">cancelled</span>. Please do not
              attempt to drop it off at a carrier location.
            </p>
          </div>
        ) : (
          <ShipmentStatusStepper currentStatus={order.shipmentStatus} />
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-b border-gray-200 pb-6">
            {/* Left: Order meta */}
            <div className="space-y-4">
              {/* Always visible */}
              <p className="text-sm text-gray-500">
                Order placed on{" "}
                <time className="font-medium text-gray-900">
                  {formattedDate}
                </time>
              </p>

              {/* Tracking / Dropoff */}
              {!isCancelled && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 mt-2 sm:mt-0">
                  {/* Track shipment button */}
                  {trackingUrl ? (
                    <button
                      onClick={() => openLinkExternal(trackingUrl)}
                      className="
          inline-flex items-center gap-2
          px-4 py-2
          rounded-full
          border border-[#0052FF]/30
          bg-[#0052FF]/5
          text-sm font-medium text-[#0052FF]
          transition-all duration-200
          hover:bg-[#0052FF]/10
          hover:border-[#0052FF]/50
          group
        "
                    >
                      <span>Track shipment</span>
                      <span
                        className="transition-transform duration-200 group-hover:translate-x-0.5"
                        aria-hidden
                      >
                        →
                      </span>
                    </button>
                  ) : (
                    <div
                      className="
          flex items-center justify-center
          px-4 py-2
          rounded-full
          border border-gray-300
          bg-gray-100
          text-sm font-medium
          text-gray-600
        "
                    >
                      Tracking not available for this shipment
                    </div>
                  )}

                  {/* Dropoff button */}
                  {order.carrier &&
                    carrierDropoffLinks[
                      order.carrier?.carrierName.toLowerCase()
                    ] && (
                      <button
                        onClick={() =>
                          openLinkExternal(
                            carrierDropoffLinks[
                              order.carrier?.carrierName.toLowerCase()
                            ]
                          )
                        }
                        className="
    inline-flex items-center gap-2
          px-4 py-2
          rounded-full
          border border-[#0052FF]/30
          bg-[#0052FF]/5
          text-sm font-medium text-[#0052FF]
          transition-all duration-200
          hover:bg-[#0052FF]/10
          hover:border-[#0052FF]/50
          group
  "
                      >
                        <span>Find Dropoff Location</span>
                        <span
                          className="transition-transform duration-200 group-hover:translate-x-0.5"
                          aria-hidden
                        >
                          →
                        </span>
                      </button>
                    )}
                </div>
              )}
            </div>

            {/* Right: Actions */}
            {canCancelOrder && (
              <div className="flex items-center">
                <CancelButton
                  text="Cancel Order"
                  disabled={!canCancelOrder}
                  onClick={() => setIsCancellationModalOpen(true)}
                  className={`${
                    !canCancelOrder ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                />
              </div>
            )}
          </div>

          {/* Shipment details section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Shipment Details
              </h3>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <ShipmentDetails order={order} />
          </div>
        </div>

        {attachments.length > 0 && (
          <Attachments attachments={attachments} isCancelled={isCancelled} />
        )}

        {/* Payment Details */}
        {paymentMethod ? (
          <PaymentDetails paymentMethod={paymentMethod} order={order} />
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-xl">
            The payment method used for this order is no longer available.
          </div>
        )}

        {/* Refund Details */}
        {order.refund && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Refund Details
              </h3>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Content */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 text-green-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Status
                  </p>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {order.refund.status}
                  </p>
                </div>
              </div>

              {/* Amount */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Refund Amount
                </p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  ${(order.refund.amount / 100).toFixed(2)}{" "}
                  {order.refund.currency.toUpperCase()}
                </p>
              </div>

              {/* Date */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Refund Date
                </p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {moment.unix(order.refund.created).format("MMMM D, YYYY")}
                </p>
              </div>
            </div>
          </div>
        )}

        <DeleteItemModal
          open={isCancellationModalOpen}
          setOpen={setIsCancellationModalOpen}
          heading="Order Cancellation"
          subheading="Are you sure you would like to cancel your order? This action cannot be undone."
          onDelete={handleOrderCancellation}
          isError={isError}
          error={error}
          isLoading={isCancelOrderLoading}
        />
      </main>
    </DashboardPageLayout>
  );
}
